import { RowState } from './dataset.js';
import { byteLength, fitBytes, normValidation, routeBeforeChange, fillCopyPlan, LEVEL_RANK } from './input.js';
import { CHECKISH, isUntouchedRow } from './grid-util.js';
export class GridInput {
    // ═══════════════════════ 검증 수준 ═══════════════════════
    /** 셀 경고·안내 (동기·서버 / 비동기 / 행 사이 중 높은 것) */
    /** @internal */ cellWarn(row, col) {
        const k = row + ':' + col;
        let best = null;
        for (const m of [this.warns, this.asyncWarn, this.rowWarns]) {
            if (!m.size)
                continue;
            const x = m.get(k);
            if (x && (!best || LEVEL_RANK[x.level] > LEVEL_RANK[best.level]))
                best = x;
        }
        return best;
    }
    /** @internal */ rowHasWarn(row) {
        const p = row + ':';
        for (const m of [this.warns, this.asyncWarn, this.rowWarns])
            for (const [k, x] of m)
                if (x.level === 'warning' && k.startsWith(p))
                    return true;
        return false;
    }
    /** 동기 검증·서버 결과를 셀에 기록 (오류 → errors, 경고·안내 → warns, 없음 → 둘 다 지움) */
    /** @internal */ setSyncMsg(row, col, m) {
        const k = row + ':' + col;
        this.errors.delete(k);
        this.warns.delete(k);
        if (!m)
            return;
        if (m.level === 'error')
            this.errors.set(k, m.message);
        else
            this.warns.set(k, m);
    }
    /** 셀 검증 수준 (오류 > 경고 > 안내, 없으면 null) */
    getCellLevel(row, col) {
        const k = row + ':' + col;
        if (this.errors.has(k) || this.asyncErr.has(k) || this.rowErrs.has(k))
            return 'error';
        return this.cellWarn(row, col)?.level ?? null;
    }
    /** 셀 검증 메시지와 수준 (가장 높은 것) */
    getCellMessage(row, col) {
        const k = row + ':' + col, e = this.errors.get(k) ?? this.asyncErr.get(k) ?? this.rowErrs.get(k);
        return e != null ? { level: 'error', message: e } : this.cellWarn(row, col);
    }
    /** 지금 표시 중인 경고·안내 목록 (level 포함, 행·컬럼 순) */
    getWarnings(level) {
        const out = new Map();
        for (const m of [this.warns, this.asyncWarn, this.rowWarns])
            for (const [k, x] of m) {
                if (level && x.level !== level)
                    continue;
                const i = k.indexOf(':'), cur = out.get(k);
                if (!cur || LEVEL_RANK[x.level] > LEVEL_RANK[cur.level ?? 'error'])
                    out.set(k, { row: +k.slice(0, i), col: k.slice(i + 1), message: x.message, level: x.level });
            }
        return [...out.values()].filter(e => this.ds.isVisibleRow(e.row)).sort((a, b) => a.row - b.row || a.col.localeCompare(b.col));
    }
    /**
     * 경고 확인 창 ("경고 n건 — 그래도 저장할까요?"). 경고가 없거나 이미 같은 경고를 확인했으면 바로 true.
     * validateAll()·saveChanges(…, { grid }) 는 warningConfirm 이면 이것을 부름 (동기 validate() 를 쓰는 화면은 직접 await)
     */
    async confirmWarnings(list = this.getWarnings('warning')) {
        if (!list.length)
            return true;
        const sig = list.map(e => e.row + ':' + e.col + ':' + e.message).join('|');
        if (sig === this.warnAckSig)
            return true;
        const wc = this.opts.warningConfirm, n = list.length.toLocaleString('ko-KR');
        const msg = (typeof wc === 'string' ? wc.replace(/\{n\}/g, n) : this.t('경고 {n}건 — 그래도 저장할까요?', { n })) + '\n' + list.slice(0, 5).map(e => `· ${this.headerOf(e.col)}: ${e.message}`).join('\n') + (list.length > 5 ? '\n…' : '');
        const id = await this.confirmDialog(this.t('저장 확인'), msg, [{ id: 'save', label: this.t('저장'), primary: true }, { id: 'cancel', label: this.t('취소') }], 'og-warnc');
        if (id !== 'save') {
            this.focusCell(list[0].row, list[0].col);
            return false;
        }
        this.warnAckSig = sig;
        return true;
    }
    // ═══════════════════════ 바이트 길이 ═══════════════════════
    /** @internal */ byteEnc() { return this.opts.byteEncoding === 'euc-kr' ? 'euc-kr' : 'utf8'; }
    /** maxBytes 를 넘으면 메시지 */
    /** @internal */ bytesError(c, v) {
        const max = c.def.maxBytes;
        if (!max || v == null || typeof v === 'boolean')
            return null;
        const n = byteLength(v, this.byteEnc());
        return n > max ? this.t('{col}은(는) 최대 {max}바이트입니다 (지금 {n}바이트)', { col: c.def.header ?? c.id, max, n }) : null;
    }
    /** 입력 중 바이트 한도: 조합이 끝난 글자 기준으로 새로 들어온 부분만 잘라 냄 (조합 중에는 자르지 않음 — 음절이 깨지지 않게) */
    /** @internal */ checkBytesInput() {
        const ed = this.editing;
        if (!ed || ed.kind === 'custom' || ed.kind === 'checkCombo')
            return;
        const c = this.cols[ed.ci], max = c?.def.maxBytes;
        if (!max)
            return;
        const v = this.ime.value, enc = this.byteEnc();
        if (byteLength(v, enc) <= max) {
            this.byteGood = v;
            this.updateByteCount();
            return;
        }
        const st = this.ime.selectionStart ?? v.length, keep = fitBytes(this.byteGood, v, max, enc);
        const cut = v.length - keep.length;
        this.ime.value = keep;
        this.byteGood = keep;
        const pos = Math.max(0, Math.min(keep.length, st - cut));
        this.ime.setSelectionRange(pos, pos);
        this.showTip(this.t('최대 {max}바이트까지 입력할 수 있습니다', { max }), 'warning');
        this.updateByteCount();
    }
    /** 편집기 바이트 수 표시 (showByteCount): 조합 중 글자도 셈, 넘으면 빨강 */
    /** @internal */ updateByteCount() {
        const ed = this.editing, c = ed && ed.kind !== 'custom' ? this.cols[ed.ci] : null;
        const on = !!c?.def.maxBytes && (c.def.showByteCount ?? !!this.opts.showByteCount);
        if (!on) {
            if (this.byteEl && !this.byteEl.hidden)
                this.byteEl.hidden = true;
            return;
        }
        if (!this.byteEl) {
            const el = document.createElement('div');
            el.className = 'og-bc';
            el.setAttribute('aria-live', 'polite');
            this.root.appendChild(el);
            this.byteEl = el;
        }
        const n = byteLength(this.ime.value, this.byteEnc()), max = c.def.maxBytes, el = this.byteEl;
        el.textContent = `${n}/${max}`;
        el.classList.toggle('og-bc-over', n > max);
        el.hidden = false;
        el.style.top = parseFloat(this.ime.style.top) + this.ime.offsetHeight + 'px';
        el.style.left = parseFloat(this.ime.style.left) + this.ime.offsetWidth - 64 + 'px';
    }
    // ═══════════════════════ 값 쓰기 전 이벤트 ═══════════════════════
    /**
     * 그리드가 값을 쓰는 모든 경로가 거치는 곳 (setCellByUser): onBeforeChanges → 셀마다 onBeforeChange·on('beforeChange') → maxBytes.
     * 남은 변경을 돌려줌. 넘친 바이트로 거부한 칸이 있으면 안내
     */
    /** @internal */ beforeChanges(changes, source, bytes = true) {
        const hasOne = !!this.opts.onBeforeChange || this.beforeChangeListeners.size > 0;
        let kept = changes;
        if (hasOne || this.opts.onBeforeChanges) {
            const one = hasOne ? (e) => {
                let r = this.opts.onBeforeChange?.(e);
                if (r === false)
                    return false;
                for (const f of this.beforeChangeListeners) {
                    const x = f(r && typeof r === 'object' ? { ...e, value: r.value } : e);
                    if (x === false)
                        return false;
                    if (x && typeof x === 'object')
                        r = x;
                }
                return r;
            } : null;
            try {
                kept = routeBeforeChange(changes, source, (row, col) => { const c = this.colInfo(col); return c ? this.valueOf(c, row) : this.ds.get(row, col); }, one, this.opts.onBeforeChanges).kept;
            }
            catch (err) {
                console.error(err);
                kept = [];
            }
        }
        // 바이트 제한: 넘치는 값은 그 칸만 거부 (편집기 확정은 이미 걸러서 옴)
        let over = 0;
        if (bytes && kept.length && this.opts.columns.some(d => d.maxBytes))
            kept = kept.filter(ch => { const c = this.colInfo(ch.col); if (c && this.bytesError(c, ch.value)) {
                over++;
                return false;
            } return true; });
        if (over) {
            this.showTip(this.t('{n}개 셀은 최대 바이트를 넘어 건너뛰었습니다', { n: over }), 'warning');
            setTimeout(() => this.hideTip(), 2500);
        }
        return kept;
    }
    /** 행 추가 전 이벤트 → 넣을 값 (취소면 null) */
    /** @internal */ beforeRowInsert(index, values, source) {
        if (!this.opts.onBeforeRowInsert && !this.beforeInsertListeners.size)
            return values;
        const e = { index, values: { ...values }, source, grid: this };
        try {
            if (this.opts.onBeforeRowInsert?.(e) === false)
                return null;
            for (const f of this.beforeInsertListeners)
                if (f(e) === false)
                    return null;
        }
        catch (err) {
            console.error(err);
            return null;
        }
        return e.values;
    }
    /** 행 삭제 전 이벤트 (false = 취소) */
    /** @internal */ beforeRowDelete(rows, source) {
        if (!this.opts.onBeforeRowDelete && !this.beforeDeleteListeners.size)
            return true;
        const e = { rows: [...rows], source, grid: this };
        try {
            if (this.opts.onBeforeRowDelete?.(e) === false)
                return false;
            for (const f of this.beforeDeleteListeners)
                if (f(e) === false)
                    return false;
        }
        catch (err) {
            console.error(err);
            return false;
        }
        return true;
    }
    /** 셀 포커스 옮기기 전 이벤트 (false = 그대로) */
    /** @internal */ beforeCellFocus(row, col, prevRow, prevCol) {
        const e = { row, col, prevRow, prevCol, grid: this };
        try {
            if (this.opts.onBeforeCellFocus?.(e) === false)
                return false;
            for (const f of this.beforeCellFocusListeners)
                if (f(e) === false)
                    return false;
        }
        catch (err) {
            console.error(err);
            return false;
        }
        return true;
    }
    // ═══════════════════════ 삭제 행 표시 ═══════════════════════
    /** 집계·내보내기용: 삭제 행 표시 중이면 rows 에서 삭제(D) 행을 뺌 (없으면 rows 그대로, 데이터 버전마다 캐시) */
    /** @internal */ liveRows(rows) {
        if (!this.opts.showDeletedRows || this.srm)
            return rows;
        const c = this.liveCache.get(rows);
        if (c && c.ver === this.ds.version)
            return c.rows;
        const st = this.ds.state;
        let n = 0;
        for (let i = 0; i < rows.length; i++)
            if (st[rows[i]] === RowState.DELETE)
                n++;
        const out = n ? rows.filter(r => st[r] !== RowState.DELETE) : rows;
        this.liveCache.set(rows, { ver: this.ds.version, rows: out });
        return out;
    }
    /** 삭제(D) 행 되살리기 — 고친 값은 그대로(UPDATE 유지). rows 없으면 선택 범위·체크한 행 중 삭제 행. 실행 취소 한 건. 되살린 행 수 */
    restoreRows(rows) {
        if (this.editing)
            this.cancelEdit();
        let target = rows;
        if (!target) {
            const { r0, r1 } = this.range();
            target = [];
            for (let vi = r0; vi <= r1; vi++) {
                const r = this.disp[vi];
                if (r >= 0)
                    target.push(r);
            }
            for (const r of this.checked)
                target.push(r);
        }
        const del = [...new Set(target)].filter(r => r >= 0 && r < this.ds.length && this.ds.rowState(r) === RowState.DELETE);
        if (!del.length)
            return 0;
        const rec = [];
        this.ds.batch(() => { for (const r of del) {
            this.ds.undeleteRow(r);
            rec.push({ row: r, before: RowState.DELETE, after: this.ds.rowState(r) });
        } });
        this.pushUndo({ label: this.t('삭제 취소 {n}건', { n: rec.length }), at: Date.now(), cells: [], rows: rec });
        this.bump();
        return rec.length;
    }
    // ═══════════════════════ 일괄 입력·채우기 ═══════════════════════
    /** 선택이 셀 2개 이상인가 (여러 범위 포함) */
    /** @internal */ multiCellSel() { const rs = this.selRanges(); return rs.length > 1 || rs[0].r0 !== rs[0].r1 || rs[0].c0 !== rs[0].c1; }
    /**
     * Ctrl+Enter (엑셀): 편집 중인 글자를 선택한 모든 칸에 — 칸마다 그 컬럼 규칙으로 해석·검증(오류 칸은 건너뜀),
     * 읽기 전용·잠금·삭제 행은 건너뜀. 실행 취소 한 건. 쓴 칸 수 (편집 중이 아니면 -1)
     */
    commitToSelection() {
        const ed = this.editing;
        if (!ed || ed.kind === 'custom')
            return -1;
        const text = ed.kind === 'checkCombo' ? null : this.ime.value;
        const ccVal = ed.kind === 'checkCombo' ? this.checkComboValue() : null;
        const changes = [], seen = new Set();
        let skipped = 0;
        for (const { r0, r1, c0, c1 } of this.selRanges())
            for (let vi = r0; vi <= r1; vi++) {
                const row = this.disp[vi];
                if (row == null || row < 0)
                    continue;
                for (let ci = c0; ci <= c1; ci++) {
                    const c = this.cols[ci], k = row + ':' + ci;
                    if (!c || c.special || seen.has(k))
                        continue;
                    seen.add(k);
                    if (!this.canEditCol(c, row))
                        continue;
                    const kind = this.editorOf(c, row);
                    if (CHECKISH.has(kind))
                        continue;
                    let p;
                    if (text == null)
                        p = kind === 'checkCombo' && c === this.cols[ed.ci] ? { value: ccVal } : kind === 'checkCombo' ? this.parseInput(c, String(ccVal ?? ''), kind, row) : { error: 'x' };
                    else
                        p = kind === 'combo' ? this.parseCombo(c, text) : this.parseInput(c, text, kind === 'button' ? 'text' : kind, row);
                    if ('error' in p) {
                        skipped++;
                        continue;
                    }
                    const vr = normValidation(c.def.validate?.(p.value, row, this.dsFor(row)));
                    if (vr?.level === 'error') {
                        skipped++;
                        continue;
                    }
                    changes.push({ row, col: c.id, value: p.value });
                }
            }
        this.endEdit(true);
        if (changes.length)
            this.setCellByUser(changes, this.t('일괄 입력'), 'fill');
        if (skipped) {
            this.showTip(this.t('{n}개 셀은 형식이 맞지 않아 건너뛰었습니다', { n: skipped }), 'warning');
            setTimeout(() => this.hideTip(), 2500);
        }
        return changes.length;
    }
    /** Ctrl+D: 선택 범위 맨 윗행 값을 아래 행들에 (한 행만 고르면 바로 위 행 값). 편집 가능한 칸만, 실행 취소 한 건. 쓴 칸 수 */
    fillDown() { return this.fillCopy('down'); }
    /** Ctrl+R: 선택 범위 맨 왼쪽 값을 오른쪽 칸들에 (한 칸 너비면 바로 왼쪽 칸 값) */
    fillRight() { return this.fillCopy('right'); }
    /** @internal */ fillCopy(dir) {
        if (!this.opts.editable)
            return 0;
        if (this.editing && !this.commitEdit())
            return 0;
        const { r0, r1, c0, c1 } = this.range();
        const rows = [];
        for (let vi = r0; vi <= r1; vi++) {
            const r = this.disp[vi];
            if (r >= 0)
                rows.push(r);
        }
        const cis = [];
        for (let ci = c0; ci <= c1; ci++)
            if (this.cols[ci] && !this.cols[ci].special)
                cis.push(ci);
        const cols = cis.map(ci => this.cols[ci].id);
        let above, left;
        if (dir === 'down' && rows.length === 1)
            for (let vi = r0 - 1; vi >= 0; vi--) {
                const r = this.disp[vi];
                if (r >= 0) {
                    above = r;
                    break;
                }
            }
        if (dir === 'right' && cols.length === 1)
            for (let ci = c0 - 1; ci >= 0; ci--) {
                const c = this.cols[ci];
                if (c && !c.special) {
                    left = c.id;
                    break;
                }
            }
        const plan = fillCopyPlan(rows, cols, dir, (r, col) => { const c = this.colInfo(col); return c ? this.valueOf(c, r) : this.ds.get(r, col); }, { above, left });
        const changes = plan.filter(x => { const c = this.colInfo(x.col); return !!c && c.ci >= 0 && this.canEditCol(c, x.row); });
        if (changes.length)
            this.setCellByUser(changes, this.t('채우기'), 'fill');
        return changes.length;
    }
    // ═══════════════════════ Esc 두 번 행 되돌리기 ═══════════════════════
    /** 행의 저장 안 한 변경을 되돌림 (수정 → 원래 값, 새 행 → 버림). 실행 취소 가능 */
    revertRowChanges(row) {
        const st = this.ds.rowState(row);
        if (st === RowState.UPDATE) {
            const ch = this.ds.columns.filter(d => this.ds.isCellChanged(row, d.id)).map(d => ({ row, col: d.id, value: this.ds.getOrig(row, d.id) }));
            if (!ch.length)
                return false;
            this.setCellByUser(ch, this.t('행 되돌리기'), 'revert', { noBuffer: true });
            return true;
        }
        if (st === RowState.INSERT) {
            this.ds.deleteRow(row);
            this.pushUndo({ label: this.t('행 되돌리기'), at: Date.now(), cells: [], rows: [{ row, before: RowState.INSERT, after: this.ds.rowState(row) }] });
            for (const k of [...this.errors.keys()])
                if (k.startsWith(row + ':'))
                    this.errors.delete(k);
            return true;
        }
        return false;
    }
    // ═══════════════════════ ↓ 새 행 ═══════════════════════
    /** 마지막 행에서 ↓ (appendRowOnDown): 새 행을 붙이고 같은 컬럼으로. 방금 붙인 행을 손대지 않았으면 더 늘리지 않음 */
    /** @internal */ appendRowDown() {
        const la = this.lastAppend, last = this.disp[this.disp.length - 1];
        if (la && la.row === last && this.ds.rowState(la.row) === RowState.INSERT && isUntouchedRow(this.ds.getRow(la.row), la.init))
            return false;
        const ci = this.focus.ci;
        const r = this.addRow(undefined, false, 'down');
        if (r < 0)
            return false;
        this.lastAppend = { row: r, init: this.ds.getRow(r) };
        const vi = this.disp.lastIndexOf(r);
        if (vi >= 0)
            this.setFocus(vi, ci, false);
        return true;
    }
}
